import React, { useEffect, useState } from 'react';
import { UploadCloud, WifiOff } from 'lucide-react';
import { usePendingWritesStore } from '../stores/pendingWritesStore';

/**
 * Header chip for connection + sync state:
 *   - offline, saves queued  → amber "Offline · N queued"
 *   - offline, nothing queued → gray "Offline"
 *   - online, saves syncing  → amber "N syncing"
 *   - online, all acked      → nothing (absence means all caught up)
 *
 * Offline state follows navigator.onLine — coarse (Wi-Fi-connected dead
 * zones read as online) but free and instant; the pending counter covers
 * the dead-zone case since queued writes show regardless.
 */
const PendingSyncBadge: React.FC = () => {
  const pending = usePendingWritesStore((s) => s.pending);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online && pending === 0) return null;

  const chipBase =
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold';

  if (!online) {
    const label = pending > 0 ? `Offline · ${pending} queued` : 'Offline';
    return (
      <span
        title="No connection. You can keep working — every save is stored on this device and uploads automatically when the signal returns."
        className={`${chipBase} ${
          pending > 0
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            : 'bg-gray-200 text-gray-700 dark:bg-dark-700 dark:text-gray-300'
        }`}
      >
        <WifiOff className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{pending > 0 ? pending : ''}</span>
      </span>
    );
  }

  return (
    <span
      title={`${pending} save${pending === 1 ? '' : 's'} waiting to sync — they're stored on this device and upload automatically when the connection returns.`}
      className={`${chipBase} bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300`}
    >
      <UploadCloud className="w-3.5 h-3.5 animate-pulse" />
      <span className="hidden sm:inline">{pending} syncing</span>
      <span className="sm:hidden">{pending}</span>
    </span>
  );
};

export default PendingSyncBadge;
